import { useId, type CSSProperties } from 'react';
import type { PublisherFieldDef, PublisherGroupDef } from '../kindSchemas';
import type { AttachmentItem } from '../types';
import { HashtagEditor } from './HashtagEditor';

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 8,
  border: '1px solid var(--border-hard)',
  background: 'var(--surface-card-a)',
  color: 'var(--text)',
};

interface PublisherGroupsProps {
  groups: PublisherGroupDef[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  hashtags: string[];
  onHashtags: (tags: string[]) => void;
  files?: Record<string, AttachmentItem[]>;
  onFiles?: (name: string, files: AttachmentItem[]) => void;
}

function optionValue(option: string | [string, string]): string {
  return Array.isArray(option) ? option[0] : option;
}

function optionLabel(option: string | [string, string]): string {
  return Array.isArray(option) ? option[1] : option;
}

function FieldControl({
  field,
  controlId,
  values,
  onChange,
  hashtags,
  onHashtags,
  files,
  onFiles,
}: {
  field: PublisherFieldDef;
  controlId: string;
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  hashtags: string[];
  onHashtags: (tags: string[]) => void;
  files?: AttachmentItem[];
  onFiles?: (files: AttachmentItem[]) => void;
}) {
  const accessibility = {
    id: controlId,
    'aria-describedby': field.hint ? `${controlId}-hint` : undefined,
  };
  if (field.type === 'file') {
    const selected = files || [];
    return (
      <div>
        <input
          {...accessibility}
          type="file"
          required={field.required && selected.length === 0}
          accept={field.accept}
          multiple={field.multiple}
          onChange={(event) => {
            const picked = Array.from(event.target.files || []).map((file) => ({
              file_id: `local-${field.name}-${file.name}-${file.size}-${file.lastModified}`,
              name: file.name,
              size: file.size,
              mime: file.type || undefined,
              purpose: field.name === 'cover' ? 'COVER' : 'ATTACHMENT',
            }));
            onFiles?.(picked);
          }}
        />
        {selected.length ? (
          <small style={{ display: 'block', marginTop: 6, color: '#86a6b6' }}>
            {selected.map((file) => file.name).join(', ')}
          </small>
        ) : null}
      </div>
    );
  }
  if (field.type === 'tags') {
    return (
      <HashtagEditor
        id={controlId}
        describedBy={accessibility['aria-describedby']}
        tags={hashtags}
        onChange={onHashtags}
      />
    );
  }
  if (field.type === 'check') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          {...accessibility}
          type="checkbox"
          required={field.required}
          checked={values[field.name] === '1'}
          onChange={(e) => onChange(field.name, e.target.checked ? '1' : '')}
        />
        {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <select
        {...accessibility}
        required={field.required}
        value={values[field.name] || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
        style={inputStyle}
      >
        <option value="">Выберите…</option>
        {(field.options || []).map((option) => (
          <option key={optionValue(option)} value={optionValue(option)}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea
        {...accessibility}
        required={field.required}
        rows={field.name === 'pollOptions' ? 4 : 3}
        value={values[field.name] || ''}
        placeholder={field.placeholder}
        onChange={(e) => onChange(field.name, e.target.value)}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    );
  }
  return (
    <input
      {...accessibility}
      required={field.required}
      type={
        field.type === 'url'
          ? 'url'
          : field.type === 'date' || field.type === 'datetime-local'
            ? field.type
            : 'text'
      }
      value={values[field.name] || ''}
      placeholder={field.placeholder}
      onChange={(e) => onChange(field.name, e.target.value)}
      style={inputStyle}
    />
  );
}

export const PublisherGroups = ({
  groups,
  values,
  onChange,
  hashtags,
  onHashtags,
  files,
  onFiles,
}: PublisherGroupsProps) => {
  const prefix = useId();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map((group, index) => (
        <section key={group.title} className="rg-form-group">
          <header>
            <b>{String(index + 1).padStart(2, '0')}</b>
            <strong>{group.title}</strong>
          </header>
          <div className="rg-form-grid">
            {group.fields.map((item) => (
              <div
                key={item.name}
                className={
                  item.type === 'textarea' ||
                  item.type === 'tags' ||
                  item.type === 'check' ||
                  item.type === 'file'
                    ? 'rg-span-2'
                    : ''
                }
              >
                {item.type !== 'check' ? (
                  <label htmlFor={`${prefix}-${item.name}`}>
                    {item.label}
                    {item.required ? ' *' : ''}
                  </label>
                ) : null}
                <FieldControl
                  field={item}
                  controlId={`${prefix}-${item.name}`}
                  values={values}
                  onChange={onChange}
                  hashtags={hashtags}
                  onHashtags={onHashtags}
                  files={files?.[item.name]}
                  onFiles={(next) => onFiles?.(item.name, next)}
                />
                {item.hint ? (
                  <small
                    id={`${prefix}-${item.name}-hint`}
                    style={{ display: 'block', marginTop: 6, color: '#86a6b6' }}
                  >
                    {item.hint}
                  </small>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
